import type { ReactNode } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LogOut,
  ChevronDown,
  Sparkles,
  Shield,
  PieChart,
  Users,
  BarChart3,
  CreditCard,
  Menu,
  X
} from "lucide-react";
import { useState, useEffect } from "react";
import { signOut } from "../lib/auth";
import { cn } from "../lib/cn";

interface AdminShellProps {
  email: string;
  children: ReactNode;
}

const adminNav = [
  { to: "/admin", label: "Overview", icon: PieChart, end: true },
  { to: "/admin/tenants", label: "Tenants", icon: Users },
  { to: "/admin/usage", label: "Usage", icon: BarChart3 },
  { to: "/admin/paid-plan-requests", label: "Paid plan requests", icon: CreditCard },
];

export function AdminShell({ email, children }: AdminShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate("/signin", { replace: true });
  };

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const displayName = email
    .split("@")[0]
    ?.split(/[._-]/)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ") ?? "User";

  const getPageTitle = () => {
    const current = adminNav.find(item => {
      if (item.to === "/admin") return location.pathname === "/admin";
      return location.pathname.startsWith(item.to);
    });
    return current ? current.label : "Admin Center";
  };

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-slate-50/60 text-slate-800">
      
      {/* 1. Desktop Sidebar (Dark Theme for Super Admin) */}
      <aside className="hidden lg:flex flex-col w-66 shrink-0 border-r border-slate-900 bg-slate-950 text-slate-200">
        {/* Brand */}
        <div className="flex h-16 items-center gap-3 px-6 border-b border-slate-900 bg-slate-950">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white text-slate-950 shadow-md">
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-bold tracking-tight text-white leading-none">Triage</span>
            <span className="mt-1 inline-flex items-center gap-0.5 text-[9px] font-semibold text-rose-300">
              <Shield className="h-2.5 w-2.5" /> Super Admin
            </span>
          </div>
        </div>

        {/* Sidebar Nav */}
        <nav className="flex-1 overflow-y-auto px-4 py-6 space-y-1">
          {adminNav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3.5 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200",
                    isActive
                      ? "bg-white text-slate-950 shadow-md"
                      : "text-slate-400 hover:bg-slate-900 hover:text-white"
                  )
                }
              >
                <Icon className="h-4.5 w-4.5 shrink-0" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* Super admin mode message */}
        <div className="p-4 border-t border-slate-900 bg-slate-950/80 text-[10px] text-slate-500 leading-normal">
          Super-admin mode — all actions audited per tenant.
        </div>
      </aside>

      {/* 2. Mobile Sidebar Overlay & Drawer */}
      {sidebarOpen && (
        <div className="lg:hidden">
          <div className="fixed inset-0 z-40 bg-slate-950/40 backdrop-blur-xs" onClick={() => setSidebarOpen(false)} />
          <aside className="fixed bottom-0 top-0 left-0 z-50 flex flex-col w-72 bg-slate-950 border-r border-slate-900 animate-fade-in text-slate-200">
            {/* Header inside drawer */}
            <div className="flex h-16 items-center justify-between px-6 border-b border-slate-900">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white text-slate-950">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-bold tracking-tight text-white leading-none">Triage</span>
                  <span className="text-[10px] font-semibold text-rose-350">Super Admin</span>
                </div>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-slate-900 text-slate-400"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Mobile Nav items */}
            <nav className="flex-1 overflow-y-auto px-4 py-6 space-y-1">
              {adminNav.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.to || (item.to !== "/admin" && location.pathname.startsWith(item.to));

                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={
                      cn(
                        "flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-all duration-200",
                        isActive
                          ? "bg-white text-slate-950 shadow-md"
                          : "text-slate-400 hover:bg-slate-900 hover:text-white"
                      )
                    }
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </nav>
          </aside>
        </div>
      )}

      {/* 3. Main Workspace Container */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        
        {/* Top Header/Navbar */}
        <header className="flex h-16 shrink-0 items-center justify-between border-b border-slate-200/80 bg-white px-4 sm:px-6 z-30">
          <div className="flex items-center gap-3">
            {/* Sidebar Toggle for Mobile */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="flex lg:hidden items-center justify-center h-10 w-10 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-600 transition-colors"
            >
              <Menu className="h-5 w-5" />
            </button>

            {/* Dynamic Page Title */}
            <div>
              <h1 className="text-base font-bold text-slate-900 leading-none">{getPageTitle()}</h1>
              <div className="hidden sm:flex items-center gap-1.5 mt-1 text-[11px] text-slate-400 font-medium">
                <span>Super Admin Console</span>
                <span>/</span>
                <span className="text-slate-655 lowercase">{getPageTitle()}</span>
              </div>
            </div>
          </div>

          {/* Topbar Right Operations */}
          <div className="flex items-center gap-4">
            
            {/* Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2 rounded-xl border border-slate-200/50 bg-slate-50/50 p-1 pr-2.5 hover:bg-slate-100/80 transition-colors"
              >
                <div className="flex h-7.5 w-7.5 items-center justify-center rounded-lg bg-linear-to-br from-rose-500 to-rose-600 text-xs font-semibold text-white">
                  SA
                </div>
                <span className="hidden sm:inline text-xs font-bold text-slate-700">{displayName}</span>
                <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
              </button>
              
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                  <div className="absolute right-0 mt-2 min-w-56 w-max max-w-xs rounded-2xl border border-slate-200 bg-white p-2 shadow-xl z-50 text-slate-800 animate-scale-in">
                    <div className="border-b border-slate-100 px-3.5 py-3">
                      <div className="text-sm font-bold text-slate-950">{displayName}</div>
                      <div className="text-xs text-slate-500 mt-1 break-all">{email}</div>
                    </div>
                    <div className="mt-1">
                      <button
                        onClick={handleSignOut}
                        className="flex w-full items-center gap-2.5 rounded-xl px-3.5 py-2.5 text-left text-sm font-semibold text-rose-600 hover:bg-rose-50 transition-colors"
                      >
                        <LogOut className="h-4 w-4 text-rose-500" />
                        Sign out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Scrollable Workspace Content */}
        <main className="flex-1 overflow-y-auto bg-slate-50/50 px-4 sm:px-6 py-8">
          <div className="mx-auto max-w-[1400px] animate-fade-in">
            {children}
          </div>
        </main>
      </div>

    </div>
  );
}
